import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';
import { toast } from 'sonner';
import { Database } from '@/integrations/supabase/types';

type CollaborativePlaylist = Database['public']['Tables']['collaborative_playlists']['Row'];
type PlaylistTrack = Database['public']['Tables']['collaborative_playlist_tracks']['Row'];

export function useCollaborativePlaylist() {
  const [playlists, setPlaylists] = useState<CollaborativePlaylist[]>([]);
  const [tracks, setTracks] = useState<PlaylistTrack[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchPlaylists = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('collaborative_playlists')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPlaylists(data as CollaborativePlaylist[] || []);
    } catch (error) {
      logger.error('Failed to fetch collaborative playlists', error);
      toast.error('Erro ao carregar playlists colaborativas');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchTracks = useCallback(async (playlistId: string) => {
    try {
      const { data, error } = await supabase
        .from('collaborative_playlist_tracks')
        .select('*')
        .eq('playlist_id', playlistId)
        .order('votes_count', { ascending: false });

      if (error) throw error;
      setTracks(data as PlaylistTrack[] || []);
    } catch (error) {
      logger.error('Failed to fetch playlist tracks', error);
    }
  }, []);

  const createPlaylist = useCallback(async (
    name: string,
    description: string,
    groupId?: string
  ) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('collaborative_playlists')
        .insert({
          name,
          description,
          created_by: user.id,
          group_id: groupId,
        })
        .select()
        .single();

      if (error) throw error;

      toast.success('Playlist colaborativa criada!');
      await fetchPlaylists();
      return data;
    } catch (error) {
      logger.error('Failed to create collaborative playlist', error);
      toast.error('Erro ao criar playlist');
      throw error;
    }
  }, [fetchPlaylists]);

  const addTrack = useCallback(async (
    playlistId: string,
    trackName: string,
    artistName: string,
    trackBpm?: number,
    trackUri?: string
  ) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('collaborative_playlist_tracks')
        .insert({
          playlist_id: playlistId,
          added_by: user.id,
          track_name: trackName,
          artist_name: artistName,
          track_bpm: trackBpm,
          track_uri: trackUri,
        });

      if (error) throw error;

      toast.success('Música adicionada!');
      await fetchTracks(playlistId);
    } catch (error) {
      logger.error('Failed to add track', error);
      toast.error('Erro ao adicionar música');
    }
  }, [fetchTracks]);

  const voteTrack = useCallback(async (
    trackId: string,
    voteType: 'up' | 'down'
  ) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('playlist_track_votes')
        .upsert({
          track_id: trackId,
          user_id: user.id,
          vote_type: voteType,
        });

      if (error) throw error;

      // Update vote count - fetch current track and update
      const { data: track, error: fetchError } = await supabase
        .from('collaborative_playlist_tracks')
        .select('votes_count, playlist_id')
        .eq('id', trackId)
        .single();

      if (fetchError) throw fetchError;

      const increment = voteType === 'up' ? 1 : -1;
      const { error: updateError } = await supabase
        .from('collaborative_playlist_tracks')
        .update({ votes_count: (track.votes_count || 0) + increment })
        .eq('id', trackId);

      if (updateError) throw updateError;

      toast.success(voteType === 'up' ? 'Voto positivo!' : 'Voto negativo');
      await fetchTracks(track.playlist_id);
    } catch (error) {
      logger.error('Failed to vote track', error);
      toast.error('Erro ao votar');
    }
  }, []);

  return {
    playlists,
    tracks,
    isLoading,
    fetchPlaylists,
    fetchTracks,
    createPlaylist,
    addTrack,
    voteTrack,
  };
}
